import { UserIcon } from '@heroicons/react/16/solid';
import { LinkIcon } from '@heroicons/react/20/solid';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { decodeHtmlEntities, unescapeMarkdownLinks } from '@cavela/ui';
import { format } from 'date-fns';
import removeMd from 'remove-markdown';
import { useAuthStore } from 'src/entities/user/store/useAuthStore';
import { TaskStatusTag, UserAvatar } from 'src/shared/components';
import { ROUTE_PATHS } from 'src/shared/config/routes';
import { Update } from 'src/shared/types/update';

import { Typography } from 'src/shared/components/ui';

import useRouteProductId from '../../hooks/useRouteProductId';
import findUserInOrganization from '../../utils/findUserInOrganization';
import getUpdateStatus from '../../utils/getUpdateStatus';
import { updateSupplierCloakLabel } from '../../utils/updateSupplierCloaks';

interface ProductUpdatesItemRowProps {
  update: Update;
}

const ProductUpdatesItemRow: React.FC<ProductUpdatesItemRowProps> = ({
  update
}) => {
  const noContent = useMemo(() => {
    const message = update?.message;
    const title = update?.title;

    return !message && !title;
  }, [update]);

  const fallBackTitle = useMemo(() => {
    const file = update?.clarification?.files?.[0]?.name;
    const link = update?.clarification?.links?.[0]?.url;

    return file || link;
  }, [update]);

  const routeProductId = useRouteProductId();
  const linkedBulkOrders = update?.entities?.bulk_orderings ?? [];
  const linkedSamples = update?.entities?.samplings ?? [];
  const supplierCloaks = updateSupplierCloakLabel(update);

  const renderMessage = (message: string) => {
    if (noContent) {
      return (
        <Typography
          variant="body-1"
          className="text-secondary-text overflow-hidden text-ellipsis whitespace-nowrap truncate"
        >
          {fallBackTitle}
        </Typography>
      );
    }

    return (
      <Typography
        variant="body-1"
        className="text-secondary-text overflow-hidden text-ellipsis whitespace-nowrap truncate"
      >
        {decodeHtmlEntities(removeMd(unescapeMarkdownLinks(message)))}
      </Typography>
    );
  };

  const selectedOrganization = useAuthStore(
    (state) => state.selectedOrganization
  );

  const repliesCount = useMemo(() => {
    let count = 0;

    count += update?.clarification?.replies?.length ?? 0;

    const isAnswered = Boolean(update?.question?.answer);

    if (isAnswered) {
      count += 1;
    }

    return count;
  }, [update?.clarification?.replies, update?.question?.answer]);

  const replyAvatar = (userId: number | null, isStaff: any) => {
    if (!userId) {
      return undefined;
    }

    if (isStaff) {
      return '/images/defaultCavela.svg';
    }

    const replyUser = userId
      ? findUserInOrganization(selectedOrganization, userId)
      : null;

    if (replyUser) {
      return replyUser.avatar_url ?? undefined;
    }
  };

  const renderReplyAvatars = () => {
    if (update?.clarification?.replies?.length > 0) {
      const uniqueReplies = new Map();

      update?.clarification?.replies?.forEach((reply) => {
        if (reply.user_id) {
          uniqueReplies.set(reply.user_id, reply);
        } else if (reply.is_staff && !uniqueReplies.has('staff')) {
          uniqueReplies.set('staff', reply);
        }
      });

      return (
        <div className="flex items-center">
          <div className="flex -space-x-3">
            {Array.from(uniqueReplies.values()).map((reply) => {
              return (
                <UserAvatar
                  size="sm"
                  key={reply.user_id || 'staff'}
                  image={
                    reply?.is_staff
                      ? '/images/defaultCavela.svg'
                      : replyAvatar(reply.user_id, reply.is_staff)
                  }
                  name={reply?.username || 'Cavela staff'}
                  disableAnimation
                  className="!border-1 !border-white"
                />
              );
            })}
          </div>
        </div>
      );
    }

    return null;
  };

  const renderStatusTag = () => {
    const type = getUpdateStatus(update);

    return <TaskStatusTag className="min-w-[75px]" type={type} />;
  };

  const isUserQuestion = useMemo(() => {
    return update?.update_type === 'user-question';
  }, [update?.update_type]);

  const questionTitle = useMemo(() => {
    return update?.title || 'Question';
  }, [update?.title]);

  return (
    <Link
      to={`${ROUTE_PATHS.PRODUCTS}/${routeProductId}?tab=updates&update=${update?.id}`}
      className="h-[80px] grid grid-cols-[auto_minmax(0,max-content)_1fr] py-4 items-start gap-3 border-b border-gray-3 cursor-pointer hover:bg-gray-1 transition-background"
      key={update?.id}
    >
      <div className="relative top-4">{renderStatusTag()}</div>

      <div className="h-full relative">
        <div className="flex gap-2 items-center">
          <Typography variant="h6" weight="bold" className="whitespace-nowrap">
            {isUserQuestion ? questionTitle : update?.title}
          </Typography>
          {isUserQuestion && (
            <UserAvatar
              size="sm"
              key={update?.clarification?.from_user_id || 'staff'}
              image={replyAvatar(update?.clarification?.from_user_id, false)}
              name={update?.clarification?.from_username || 'Cavela staff'}
              disableAnimation
              className="!border-1 !border-white"
            />
          )}
        </div>
        <div className="flex items-center gap-2 absolute w-full left-0 right-0">
          {supplierCloaks && (
            <>
              <div className="flex items-center gap-1">
                <div className="flex items-center gap-[2.5px]">
                  <UserIcon className="size-4 text-secondary-text" />
                  <Typography
                    variant="body-2"
                    className="text-secondary-text whitespace-nowrap"
                    weight="medium"
                  >
                    {supplierCloaks}
                  </Typography>
                </div>
              </div>
              <div className="min-h-[6px] min-w-[6px] bg-gray-4 rounded-full" />
            </>
          )}

          <Typography
            variant="body-2"
            className="text-secondary-text whitespace-nowrap"
            weight="medium"
          >
            {format(update?.created_at, 'MMM dd, yyyy')}
          </Typography>

          <div className="min-h-[6px] min-w-[6px] bg-gray-4 rounded-full" />
          <div className="flex items-center gap-1">
            {renderReplyAvatars()}
            <Typography
              variant="body-2"
              className="text-secondary-text whitespace-nowrap"
              weight="medium"
            >
              {repliesCount} {repliesCount === 1 ? 'Reply' : 'Replies'}
            </Typography>
          </div>

          {linkedBulkOrders.map((linkedBulkOrder) => (
            <React.Fragment key={linkedBulkOrder.id}>
              <div className="min-h-[6px] min-w-[6px] bg-gray-4 rounded-full" />
              <Link
                to={`${ROUTE_PATHS.PRODUCTS}/${linkedBulkOrder.product_id ?? routeProductId}?tab=orders&order=${linkedBulkOrder.id}`}
                className="flex items-center gap-[2.5px] cursor-pointer text-secondary-text hover:text-primary"
              >
                <LinkIcon className="size-4 text-inherit" />
                <Typography
                  variant="body-2"
                  className="whitespace-nowrap text-inherit"
                  weight="medium"
                >
                  {linkedBulkOrder.name}
                </Typography>
              </Link>
            </React.Fragment>
          ))}

          {linkedSamples.map((linkedSample) => (
            <React.Fragment key={linkedSample.id}>
              <div className="min-h-[6px] min-w-[6px] bg-gray-4 rounded-full" />
              <Link
                to={`${ROUTE_PATHS.PRODUCTS}/${linkedSample.product_id ?? routeProductId}?tab=samples&sample=${linkedSample.id}`}
                className="flex items-center gap-[2.5px] cursor-pointer text-secondary-text hover:text-primary"
              >
                <LinkIcon className="size-4 text-inherit" />
                <Typography
                  variant="body-2"
                  className="whitespace-nowrap text-inherit"
                  weight="medium"
                >
                  {linkedSample.name}
                </Typography>
              </Link>
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="truncate overflow-hidden">
        {renderMessage(update?.message)}
      </div>
    </Link>
  );
};

export default ProductUpdatesItemRow;
